(ns app.front.view.pdf-markup.stamp-subs
  (:require [app.domain.group.model :as group-model]
            [app.domain.stamp.model :as stamp-model]
            [app.front.entities :as entities]
            [app.front.stamp.render :as render]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.date-util :as date-util]
            [core.front.event :refer [>evt]]
            [re-frame.core-instrumented :as rf]))

(defn busy? [db] (get-in db [::state :busy?]))

(defn- group-id [db workspace-id]
  (some-> (entities/find-entity (entities/db->context db) {:workspace/id workspace-id})
          group-model/get-authority-group
          :db/id))

(defn- with-context [picker context]
  (when picker
    (assoc picker
           :context context :loading? false :error nil
           :selected-id (if (some #(= (:selected-id picker) (:id %)) (:stamps context))
                          (:selected-id picker)
                          (-> context :stamps first :id))
           ;; A cleared date is intentional; initialize only when the rendering context first arrives.
           :date (if (contains? picker :date)
                   (:date picker)
                   (.format date-util/calendar-date-format (js/Date.))))))

(rf/reg-event-fx ::initialize
  (fn [{:keys [db]} [_ workspace-id]]
    (let [new-db (assoc db ::state {:workspace-id workspace-id})]
      (if (and (not= :fetching (get-in db [:view/workspace :fetch-status])) (group-id db workspace-id))
        {:db new-db
         :fx [[:dispatch [::load]]]}
        {:db         new-db
         ;; On a cold route entry the parent controller is still loading the workspace.
         ;; Fetch status changes after its entities have been merged. Leaving cancels this wait.
         :async-flow {:rules [{:when     :seen?
                               :events   [::ws-subs/set-fetch-status]
                               :dispatch [::load]
                               :halt?    true}
                              {:when   :seen?
                               :events [::clear]
                               :halt?  true}]}}))))

(rf/reg-event-db ::clear
  (fn [db _] (dissoc db ::state)))

(rf/reg-event-fx ::load
  (fn [{:keys [db]} _]
    (let [{:keys [workspace-id load-id]} (::state db)
          group-id                       (when workspace-id (group-id db workspace-id))]
      (when (and group-id (not load-id))
        (let [request-id (random-uuid)
              failed     [::load-failed request-id]]
          {:db           (update db ::state assoc :group-id group-id :load-id request-id)
           :comm/command {:command    :stamp/list
                          :method     :get
                          :data       {:authority-group-id group-id}
                          :on-success [::loaded request-id]
                          :on-done    failed
                          :on-error   failed}})))))

(rf/reg-event-db ::loaded
  (fn [db [_ request-id _status context]]
    (if (= request-id (get-in db [::state :load-id]))
      (update db ::state #(-> % (assoc :context context :load-id nil) (update :picker with-context context)))
      db)))

(rf/reg-event-db ::load-failed
  (fn [db [_ request-id _status]]
    (if (= request-id (get-in db [::state :load-id]))
      ;; Failed refreshes invalidate cached metadata and any composition based on it.
      (update db ::state #(-> %
                              (assoc :context nil :load-id nil :request-id nil :busy? false)
                              (update :picker (fn [picker]
                                                (when picker
                                                  (assoc picker :context nil :loading? false
                                                         :error :stamp/load-error))))))
      db)))

(rf/reg-event-fx ::open
  (fn [{:keys [db]} _]
    (when (and (::state db) (not (busy? db)))
      {:db (update db ::state
                   #(assoc % :picker (if-let [context (:context %)]
                                       (with-context {} context)
                                       {:loading? true})))
       :fx [[:dispatch [::load]]]})))

(rf/reg-event-db ::close
  (fn [db _]
    (if (::state db)
      (update db ::state assoc :picker nil :busy? false :request-id nil)
      db)))

(rf/reg-event-db ::select
  (fn [db [_ id]]
    (if (and (get-in db [::state :picker]) (not (busy? db)))
      (assoc-in db [::state :picker :selected-id] id)
      db)))

(rf/reg-event-db ::date
  (fn [db [_ date]]
    (if (and (get-in db [::state :picker]) (not (busy? db)))
      (assoc-in db [::state :picker :date] date)
      db)))

(rf/reg-fx ::compose
  (fn [{:keys [request-id snapshot annotation]}]
    (-> (js/Promise.resolve)
        (.then #(render/compose! snapshot))
        (.then #(.arrayBuffer %))
        (.then #(>evt [::composed request-id annotation (:layout snapshot) (js/Uint8Array. %)]))
        (.catch #(>evt [::render-failed request-id])))))

(rf/reg-event-fx ::apply
  (fn [{:keys [db]} [_ annotation]]
    (let [{:keys [picker group-id]}          (::state db)
          {:keys [context selected-id date]} picker
          {:keys [layout assets] :as stamp}  (some #(when (= selected-id (:id %)) %) (:stamps context))]
      (when (and picker stamp (not (busy? db)) (stamp-model/valid-layout? layout)
                 (or (not (stamp-model/has-type? layout :date)) date))
        (let [request-id (random-uuid)]
          {:db       (-> db
                         (update ::state assoc :request-id request-id :busy? true)
                         (assoc-in [::state :picker :error] nil))
           ::compose {:request-id request-id
                      :annotation annotation
                      :snapshot   (merge context {:layout     layout
                                                  :date       date
                                                  :image-urls (stamp-model/image-urls group-id assets)})}})))))

(rf/reg-fx ::arm
  (fn [{:keys [request-id annotation layout bytes]}]
    (-> (js/Promise.resolve)
        (.then #(.arm (.-stamps ^js annotation)
                      #js {:source        bytes
                           :targetWidth   (:width layout)
                           :intrinsicSize #js {:width  (:width layout)
                                               :height (:height layout)}}))
        (.then #(>evt [::applied request-id]))
        (.catch #(>evt [::render-failed request-id])))))

(rf/reg-event-fx ::composed
  (fn [{:keys [db]} [_ request-id annotation layout bytes]]
    (when (= request-id (get-in db [::state :request-id]))
      {::arm {:request-id request-id :annotation annotation :layout layout :bytes bytes}})))

(rf/reg-event-db ::applied
  (fn [db [_ request-id]]
    (if (= request-id (get-in db [::state :request-id]))
      (update db ::state assoc :picker nil :busy? false :request-id nil)
      db)))

(rf/reg-event-db ::render-failed
  (fn [db [_ request-id]]
    (if (= request-id (get-in db [::state :request-id]))
      (-> db
          (update ::state assoc :busy? false :request-id nil)
          (assoc-in [::state :picker :error] :stamp/render-error))
      db)))

(rf/reg-sub ::state :-> ::state)
(rf/reg-sub ::busy? :-> busy?)
